import { styled, SxProps, TextFieldProps, Theme } from '@mui/material';
import { DesktopDatePicker, DesktopDatePickerProps } from '@mui/x-date-pickers';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isNil from 'lodash/isNil';
import React, { ReactElement, ReactNode, useRef } from 'react';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { applyWarningSx, getUtcValue, resolveDatePickerValue } from '@/components/hook-form/utils';
import WarningHelperText from '@/components/hook-form/WarningHelperText';
import { mergeSx } from '@/utils/cssStyles';

type CustomPickersDayProps = PickersDayProps<Dayjs> & {
  isHighlighted?: boolean;
};

const CustomPickersDay = styled((props: CustomPickersDayProps) => {
  const { isHighlighted, ...other } = props;
  return <PickersDay {...other} />;
})(({ isHighlighted, theme }) => ({
  ...(isHighlighted && {
    borderColor: theme.palette.grey[600],
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: '50%',
  }),
}));

dayjs.extend(utc);

//  Done via: https://mui.com/x/react-date-pickers/adapters-locale/#with-dayjs

type IPropsBase<T extends FieldValues> = {
  name: FieldPath<T>;
  size?: TextFieldProps['size'];
  sx?: SxProps<Theme>;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  onChange?: (value: Date | null) => void;
  overrideValue?: Date | string | null;
  customButton?: ReactElement;
  calenderStartDate?: Date;
  disableHighlightToday?: boolean;
  textFieldHelperText?: ReactNode;
  minDate?: Date | Dayjs;
  maxDate?: Date | Dayjs;
  warningText?: string;
};

type ControlledComponent<T extends FieldValues> = IPropsBase<T> & { open: boolean; setOpen: (open: boolean) => void };
type UncontrolledComponent<T extends FieldValues> = IPropsBase<T> & { open?: never; setOpen?: never };

type IProps<T extends FieldValues> = ControlledComponent<T> | UncontrolledComponent<T>;

export default function RHFDatePicker<T extends FieldValues>({
  name,
  size = 'medium',
  sx,
  onChange,
  overrideValue,
  customButton,
  startAdornment = null,
  endAdornment = null,
  open,
  setOpen,
  calenderStartDate,
  disableHighlightToday,
  textFieldHelperText,
  minDate,
  maxDate,
  warningText,
  disabled,
  ...other
}: IProps<T> & Omit<DesktopDatePickerProps<Dayjs>, 'onChange' | 'value' | 'minDate' | 'maxDate'>) {
  const { control } = useFormContext();
  const baseSx = mergeSx({ width: '100%' }, sx);
  const effectiveSx = applyWarningSx(warningText, baseSx);

  // Holds the value the user is currently typing. It is only committed to the form on accept
  // (calendar pick / Enter) or blur — committing on every keystroke submits half-typed dates
  // (e.g. year "2" while typing "2026") to auto-saving forms and steals focus.
  // `undefined` means there is no pending edit.
  const pendingValueRef = useRef<Dayjs | null | undefined>(undefined);

  const isStringOverride = typeof overrideValue === 'string' && resolveDatePickerValue(overrideValue) === null;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        let helperTextContent: ReactNode;
        if (error) {
          helperTextContent = error.message;
        } else if (warningText) {
          helperTextContent = <WarningHelperText text={warningText} />;
        } else {
          helperTextContent = textFieldHelperText;
        }
        const commitValue = (value: Dayjs | null) => {
          const fixedValue = !isNil(value) && value.isValid() ? getUtcValue(value.toDate()) : null;
          // Skip no-op commits (e.g. onAccept firing when the calendar is closed without a
          // change) so auto-saving consumers don't submit spuriously.
          const currentValue = resolveDatePickerValue(overrideValue ?? field.value);
          const nextValue = isNil(fixedValue) ? null : resolveDatePickerValue(fixedValue);
          const isUnchanged = isNil(nextValue)
            ? isNil(currentValue)
            : !isNil(currentValue) && nextValue.isSame(currentValue, 'day');
          if (isUnchanged) return;
          field.onChange(fixedValue);
          onChange?.(fixedValue);
        };
        return (
          <DesktopDatePicker
            value={resolveDatePickerValue(overrideValue ?? field.value)}
            disabled={disabled}
            onChange={(value: Dayjs | null) => {
              pendingValueRef.current = value;
            }}
            onAccept={(value: Dayjs | null) => {
              pendingValueRef.current = undefined;
              commitValue(value);
            }}
            open={open}
            onOpen={() => setOpen?.(true)}
            onClose={() => setOpen?.(false)}
            disableHighlightToday={disableHighlightToday}
            referenceDate={calenderStartDate ? dayjs(calenderStartDate) : undefined}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            slots={{
              day: CustomPickersDay,
              ...(customButton ? { actionBar: () => customButton } : {}),
            }}
            slotProps={{
              day: (ownerState) =>
                ({
                  isHighlighted: calenderStartDate
                    ? dayjs(ownerState.day).isSame(dayjs(calenderStartDate), 'day')
                    : false,
                }) as Partial<PickersDayProps<Dayjs>>,
              textField: {
                name,
                sx: effectiveSx,
                size,
                disabled: disabled || (field.value === null && !isNil(overrideValue)),
                inputRef: field.ref,
                error: !!error,
                helperText: helperTextContent,
                onBlur: () => {
                  if (pendingValueRef.current !== undefined) {
                    commitValue(pendingValueRef.current);
                    pendingValueRef.current = undefined;
                  }
                },
                InputProps: {
                  ...(startAdornment !== null && { startAdornment }),
                  ...(endAdornment !== null && { endAdornment }),
                  autoComplete: 'off',
                },
                ...(isStringOverride ? { inputProps: { value: overrideValue } } : {}),
              },
            }}
            {...other}
          />
        );
      }}
    />
  );
}
